import React, {
  useState,
  useMemo,
  useCallback,
  Suspense,
  lazy,
  useEffect,
  useRef,
} from "react";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import { debounce } from "lodash";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "../components/Calendar.css";
import { rooms } from "../constants/rooms";
import { getProductivityByRoom, getWorkOrderStats } from "../api/workOrder";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Bar = lazy(() => import("react-chartjs-2").then((module) => ({ default: module.Bar })));
const CustomCalendar = lazy(() => import("./Calender"));

// Utility functions (unchanged)
const isLeapYear = (year) =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const getDaysInMonth = (year, month) => {
  if (month === 1) return isLeapYear(year) ? 29 : 28;
  return [31, null, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
};
const calculateDateRange = (filter, today) => {
  const normalizedToday = today;
 const yesterday = new Date(normalizedToday);
  yesterday.setDate(yesterday.getDate() - 1);  let start, end;
  switch (filter) {
    case "today":
      start = normalizedToday;
      end = normalizedToday;
      break;
    case "yesterday":
      start = yesterday;
      end = yesterday;
      break;
    case "last 7 days":
      start = new Date(normalizedToday);
      start.setDate(normalizedToday.getDate() - 6);
      end = normalizedToday;
      break;
    case "last 30 days":
      start = new Date(normalizedToday);
      start.setDate(normalizedToday.getDate() - 29);
      end = normalizedToday;
      break;
    case "last 90 days":
      start = new Date(normalizedToday);
      start.setDate(normalizedToday.getDate() - 89);
      end = normalizedToday;
      break;
    case "last 365 days":
      start = new Date(normalizedToday);
      start.setDate(start.getDate() - 364);
      end = normalizedToday;
      break;
    default:
      start = new Date(normalizedToday);
      start.setDate(normalizedToday.getDate() - 29);
      end = normalizedToday;
      break;
  }
  return { start, end };
};

const Analytics = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [dateFilter, setDateFilter] = useState("last 30 days");
  const [customDateRange, setCustomDateRange] = useState(calculateDateRange("last 30 days", today));
  const [sortConfig, setSortConfig] = useState({ key: "output", direction: "descending" });
  const [productivityData, setProductivityData] = useState([]);
  const [workOrderStats, setWorkOrderStats] = useState({
    assignedInPeriod: 0,
    totalUnassignedBacklog: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchParams = useRef(null);

  const formatDateForAPI = useCallback((date) => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const period = calculateDateRange(dateFilter, today);
      const startDate = formatDateForAPI(period.start);
      const endDate = formatDateForAPI(period.end);

      const currentParams = `${startDate}-${endDate}-${dateFilter}`;
      if (lastFetchParams.current === currentParams) return;

      setLoading(true);
      setError(null);
      try {
        const [productivityResponse, statsResponse] = await Promise.all([
          getProductivityByRoom(startDate, endDate),
          getWorkOrderStats(startDate, endDate),
        ]);

        setProductivityData(productivityResponse.data || []);
        setWorkOrderStats({
          assignedInPeriod: statsResponse.assignedInPeriod || 0,
          totalUnassignedBacklog: statsResponse.totalUnassignedBacklog || 0,
        });

        lastFetchParams.current = currentParams;
        toast.success("Data loaded successfully", { position: "top-right", duration: 2000 });
      } catch (err) {
        setError("Failed to load data");
        toast.error("Failed to load data", { position: "top-right", duration: 2000 });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateFilter, customDateRange.start, customDateRange.end, formatDateForAPI]);

  // Key Metrics
  const totalCompleted = useMemo(() => 
    productivityData.find(item => item.Room === "Total")?.CompletedWorkOrders || 0,
    [productivityData]
  );

  const qtyMetrics = useMemo(() => {
    let planned = 0;
    let actual = 0;
    productivityData.forEach(item => {
      if (item.Room === "Total") {
        planned = item.PlannedQty || 0;
        actual = item.ActualQty || 0;
      }
    });
    const efficiency = planned === 0 ? 0 : Math.round((actual / planned) * 100);
    return { planned, actual, efficiency };
  }, [productivityData]);

  const assignmentRate = useMemo(() => {
    if (totalCompleted === 0) return 0;
    return Math.round((workOrderStats.assignedInPeriod / totalCompleted) * 100);
  }, [workOrderStats.assignedInPeriod, totalCompleted]);

  // Current Period Only Metrics
  const currentMetrics = useMemo(() => {
    const metrics = { overall: totalCompleted, rooms: {} };
    rooms.forEach(room => (metrics.rooms[room] = 0));
    productivityData.forEach(item => {
      if (item.Room !== "Total") {
        metrics.rooms[item.Room] = item.CompletedWorkOrders || 0;
      }
    });
    return metrics;
  }, [productivityData, totalCompleted, rooms]);

  const sortedRoomMetrics = useMemo(() => {
    return rooms
      .filter(room => room !== "Total")
      .map(room => ({
        room,
        completed: currentMetrics.rooms[room] || 0,
        plannedQty: productivityData.find(p => p.Room === room)?.PlannedQty || 0,
        actualQty: productivityData.find(p => p.Room === room)?.ActualQty || 0,
      }))
      .sort((a, b) => {
        if (sortConfig.key === "room") return sortConfig.direction === "descending"
          ? b.room.localeCompare(a.room)
          : a.room.localeCompare(b.room);
        return sortConfig.direction === "descending"
          ? b[sortConfig.key] - a[sortConfig.key]
          : a[sortConfig.key] - b[sortConfig.key];
      });
  }, [currentMetrics, productivityData, sortConfig]);

  const requestSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "descending" ? "ascending" : "descending",
    }));
  }, []);

  const exportToExcel = useCallback((data, sheetName) => {
    if (data.length === 0) return toast.error("No data to export", { position: "top-right", duration: 2000 });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${sheetName.toLowerCase().replace(/ /g, "-")}-report.xlsx`);
    toast.success("Exported successfully", { position: "top-right", duration: 2000 });
  }, []);

  // Chart 1: Planned vs Actual Quantity by Room
  const qtyChartData = useMemo(() => ({
    labels: sortedRoomMetrics.map(r => r.room),
    datasets: [
      {
        label: "Planned Quantity",
        data: sortedRoomMetrics.map(r => r.plannedQty),
        backgroundColor: "#93c5fd",
      },
      {
        label: "Actual Quantity",
        data: sortedRoomMetrics.map(r => r.actualQty),
        backgroundColor: "#2563eb",
      },
    ],
  }), [sortedRoomMetrics]);

  // Chart 2: Completed Work Orders by Room
  const completedChartData = useMemo(() => ({
    labels: sortedRoomMetrics.map(r => r.room),
    datasets: [{
      label: "Completed Work Orders",
      data: sortedRoomMetrics.map(r => r.completed),
      backgroundColor: "#10b981",
    }],
  }), [sortedRoomMetrics]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`
        }
      }
    },
    scales: { x: { stacked: true }, y: { stacked: true } },
  };

  const completedChartOptions = {
    ...chartOptions,
    scales: { x: { stacked: false }, y: { stacked: false } },
  };

  return (
    <Suspense fallback={<div className="text-center py-10">Loading Analytics...</div>}>
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        <Toaster />
        <h3 className="text-2xl font-bold text-gray-900 mb-8">Production Analytics</h3>

        <div className="mb-8">
          <CustomCalendar
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            dateRange={customDateRange}
            onDateRangeChange={() => {}}
            value={customDateRange.start || today}
            className="w-full max-w-md"
          />
        </div>

        {/* Key Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-blue-50 p-6 rounded-lg text-center border border-blue-200">
            <p className="text-sm font-medium text-blue-800">Scheduled</p>
            <p className="text-4xl font-bold text-blue-900 mt-3">
              {workOrderStats.assignedInPeriod.toLocaleString()}
            </p>
            <p className="text-xs text-blue-600 mt-2">Work orders assigned in period</p>
          </div>

          <div className="bg-green-50 p-6 rounded-lg text-center border border-green-200">
            <p className="text-sm font-medium text-green-800">Completed</p>
            <p className="text-4xl font-bold text-green-900 mt-3">
              {totalCompleted.toLocaleString()}
            </p>
            <p className="text-xs text-green-600 mt-2">Work orders finished</p>
          </div>

          <div className="bg-purple-50 p-6 rounded-lg text-center border border-purple-200">
            <p className="text-sm font-medium text-purple-800">Production Efficiency</p>
            <p className="text-4xl font-bold text-purple-900 mt-3">
              {qtyMetrics.efficiency}%
            </p>
            <p className="text-xs text-purple-600 mt-2">
              {qtyMetrics.actual.toLocaleString()} / {qtyMetrics.planned.toLocaleString()} units
            </p>
          </div>

          <div className="bg-red-50 p-6 rounded-lg text-center border border-red-200">
            <p className="text-sm font-medium text-red-800">Unassigned Backlog</p>
            <p className="text-4xl font-bold text-red-900 mt-3">
              {workOrderStats.totalUnassignedBacklog.toLocaleString()}
            </p>
            <p className="text-xs text-red-600 mt-2">Need scheduling</p>
          </div>
        </div>

        {loading && (
          <div className="text-center py-20">
            <ArrowPathIcon className="mx-auto h-12 w-12 text-blue-600 animate-spin" />
            <p className="mt-4 text-lg text-gray-600">Loading analytics data...</p>
          </div>
        )}

        {error && <div className="text-red-600 text-center py-10 text-lg">{error}</div>}

        {!loading && !error && (
          <>
            {/* Table */}
            <div className="bg-white rounded-lg shadow border border-gray-200 mb-8">
              <div className="p-6 flex justify-between items-center">
                <h4 className="text-lg font-semibold text-gray-900">Completed by Room</h4>
                <button
                  onClick={() => exportToExcel(
                    sortedRoomMetrics.map(r => ({
                      Room: r.room,
                      "Completed Orders": r.completed,
                      "Planned Qty": r.plannedQty,
                      "Actual Qty": r.actualQty,
                    })),
                    "Production Report"
                  )}
                  className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 text-sm font-medium"
                >
                  Export to Excel
                </button>
              </div>
              <div className="overflow-x-auto px-6 pb-6">
                {sortedRoomMetrics.every(r => r.completed === 0) ? (
                  <div className="text-center py-12 text-gray-500">No production in selected period</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th onClick={() => requestSort("room")} className="text-left p-4 cursor-pointer hover:bg-gray-100">Room</th>
                        <th onClick={() => requestSort("completed")} className="text-left p-4 cursor-pointer hover:bg-gray-100">Completed Orders</th>
                        <th onClick={() => requestSort("plannedQty")} className="text-left p-4 cursor-pointer hover:bg-gray-100">Planned Qty</th>
                        <th onClick={() => requestSort("actualQty")} className="text-left p-4 cursor-pointer hover:bg-gray-100">Actual Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRoomMetrics.map(room => (
                        <tr key={room.room} className="border-b hover:bg-gray-50">
                          <td className="p-4 font-medium">{room.room}</td>
                          <td className="p-4">{room.completed}</td>
                          <td className="p-4">{room.plannedQty.toLocaleString()}</td>
                          <td className="p-4 font-medium">{room.actualQty.toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr className="font-bold bg-gray-100">
                        <td className="p-4">Total</td>
                        <td className="p-4">{totalCompleted}</td>
                        <td className="p-4">{qtyMetrics.planned.toLocaleString()}</td>
                        <td className="p-4">{qtyMetrics.actual.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Planned vs Actual Quantity */}
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-6">
                  Planned vs Actual Quantity by Room
                </h4>
                <div style={{ height: "400px" }}>
                  {qtyMetrics.planned === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      No quantity data available
                    </div>
                  ) : (
                    <Bar data={qtyChartData} options={chartOptions} />
                  )}
                </div>
              </div>

              {/* Completed Work Orders */}
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-6">
                  Completed Work Orders by Room
                </h4>
                <div style={{ height: "400px" }}>
                  {totalCompleted === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      No completions in period
                    </div>
                  ) : (
                    <Bar data={completedChartData} options={completedChartOptions} />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Suspense>
  );
};

export default React.memo(Analytics);